import { getCurrencyDecimals, getSubunitMultiplier } from '@bbpos/utils';
import { useTenantCurrency, getCurrencySymbol } from '@/hooks/use-tenant-currency';
import { useTaxRegime } from '@/hooks/use-tax-regime';
import { useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { PageContainer, Button, Skeleton, Modal, Input, Select, ErrorBoundary, Timeline, useToast } from '@bbpos/ui';
import type { TimelineItem } from '@bbpos/ui';
import { formatCurrency } from '@/utils/currency';
import { GLASS, GLASS_FORM } from '@/constants/design-tokens';
import {
  useRfq, useSendRfq, useSubmitRfqQuote, useCloseRfq, useCancelRfq, useDownloadRfqPdf, useAwardRfq,
  RfqStatusBadge, RfqResponseBadge, quoteCell, lowestForItem, supplierTotal, defaultAwards, awardSummary,
} from '@/features/rfqs';
import type { RfqSupplier } from '@/features/rfqs';
import type { PoDiscountType } from '@/features/purchase-orders';
import { taxRateOptions } from '@/features/purchase-orders/line-totals';
import { useAuthStore } from '@/stores/use-auth-store';
import { ROLE_LEVELS } from '@/types/user';
import { SendIcon, BanIcon, CheckIcon, DownloadIcon, ClipboardIcon } from '../purchase-orders/icons';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const STATUS_DESCRIPTIONS: Record<string, string> = {
  DRAFT: 'Not sent yet. Edit the items and suppliers, then send it out for quotes.',
  SENT: 'Sent to suppliers. Record their quotes as they come in, pick the winning supplier for each line, then create the purchase orders.',
  CLOSED: 'Closed — no further purchase orders can be raised from this RFQ.',
  CANCELLED: 'This RFQ has been cancelled.',
};

interface QuoteLineForm {
  unit_cost: string;
  gst_rate: string;
  discount_type: PoDiscountType;
  /** % for PERCENTAGE, major units per unit for FLAT — scaled on save (bp / minor units) */
  discount_value: string;
}
const blankQuoteLine = (): QuoteLineForm => ({ unit_cost: '', gst_rate: '0', discount_type: 'PERCENTAGE', discount_value: '' });

function fmtDate(value: string | null | undefined, withTime = false) {
  if (!value) return '—';
  const d = new Date(value);
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', ...(withTime ? { hour: '2-digit', minute: '2-digit' } : {}) });
}

function RfqDetailContent() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const currency = useTenantCurrency();
  const tax = useTaxRegime();
  const symbol = getCurrencySymbol(currency);
  const subunit = getSubunitMultiplier(currency);
  const dp = getCurrencyDecimals(currency);
  const profile = useAuthStore((s) => s.profile);
  const roleLevel = ROLE_LEVELS[profile?.role ?? ''] ?? 0;
  const isOwner = roleLevel >= ROLE_LEVELS.OWNER;
  const isManager = roleLevel >= ROLE_LEVELS.MANAGER;

  const { data: rfq, isLoading, error } = useRfq(id);
  const sendMutation = useSendRfq();
  const quoteMutation = useSubmitRfqQuote();
  const closeMutation = useCloseRfq();
  const cancelMutation = useCancelRfq();
  const pdfMutation = useDownloadRfqPdf();
  const awardMutation = useAwardRfq();

  // Send modal: per-supplier editable email
  const [showSend, setShowSend] = useState(false);
  const [sendMode, setSendMode] = useState<'EMAIL' | 'MANUAL'>('EMAIL');
  const [sendEmails, setSendEmails] = useState<Record<string, string>>({});
  // Quote modal
  const [quoteFor, setQuoteFor] = useState<RfqSupplier | null>(null);
  const [quoteLines, setQuoteLines] = useState<Record<string, QuoteLineForm>>({});
  const [quoteLeadTime, setQuoteLeadTime] = useState('');
  const [quoteNotes, setQuoteNotes] = useState('');
  // Cancel modal
  const [showCancel, setShowCancel] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  // Award (QA PU1-003): winning rfq_supplier per line, '' = not awarded. Starts at
  // the lowest quote per line; the buyer's picks override it.
  const [awardPicks, setAwardPicks] = useState<Record<string, string>>({});
  const [showAward, setShowAward] = useState(false);

  const lowest = useMemo(() => {
    if (!rfq) return new Map<string, string[]>();
    return new Map(rfq.items.map((i) => [i.id, lowestForItem(i, rfq.suppliers, currency)]));
  }, [rfq, currency]);
  const awards = useMemo(
    () => (rfq ? { ...defaultAwards(rfq.items, rfq.suppliers, currency), ...awardPicks } : {}),
    [rfq, currency, awardPicks],
  );
  const awardGroups = useMemo(
    () => (rfq ? awardSummary(rfq.items, rfq.suppliers, awards, currency) : []),
    [rfq, awards, currency],
  );

  if (error) {
    toast({ title: (error as Error).message, variant: 'error' });
    navigate('/inventory/rfqs');
    return null;
  }
  if (isLoading || !rfq) {
    return (
      <>
        <Skeleton variant="rect" height={96} className="rounded-xl mb-6" />
        <Skeleton variant="rect" height={320} className="rounded-xl" />
      </>
    );
  }

  const canEdit = rfq.status === 'DRAFT' && isManager;
  const canSend = (rfq.status === 'DRAFT' || rfq.status === 'SENT') && isManager;
  const canQuote = rfq.status === 'SENT' && isManager;
  const canAward = rfq.status === 'SENT' && isManager && rfq.suppliers.some((s) => s.quotes.length > 0);
  const isAwarded = rfq.items.some((i) => i.awarded_rfq_supplier_id);
  const canClose = rfq.status === 'SENT' && isManager;
  const canCancel = (rfq.status === 'DRAFT' || rfq.status === 'SENT') && isOwner;

  function openSend() {
    if (!rfq) return;
    setSendEmails(Object.fromEntries(rfq.suppliers.map((s) => [s.id, s.supplier_email ?? ''])));
    setShowSend(true);
  }
  function handleSend() {
    if (!rfq) return;
    if (sendMode === 'MANUAL') {
      sendMutation.mutate({ id: rfq.id, payload: { channel: 'MANUAL' } }, {
        onSuccess: () => { setShowSend(false); toast({ title: 'Marked as sent - you can now record quotes and raise POs', variant: 'success' }); },
        onError: (err: Error) => toast({ title: err.message, variant: 'error' }),
      });
      return;
    }
    const recipients = rfq.suppliers
      .filter((s) => EMAIL_RE.test(sendEmails[s.id] ?? '') && (sendEmails[s.id] ?? '') !== (s.supplier_email ?? ''))
      .map((s) => ({ rfq_supplier_id: s.id, email: sendEmails[s.id] }));
    sendMutation.mutate({ id: rfq.id, payload: { channel: 'EMAIL', recipients } }, {
      onSuccess: (res) => {
        setShowSend(false);
        const skipped = res.skipped.length ? ` · skipped ${res.skipped.map((s) => s.supplier_name).join(', ')} (no email)` : '';
        toast({ title: `Sent to ${res.sent.length} supplier${res.sent.length === 1 ? '' : 's'}${skipped}`, variant: res.skipped.length ? 'warning' : 'success' });
      },
      onError: (err: Error) => toast({ title: err.message, variant: 'error' }),
    });
  }

  function openQuote(s: RfqSupplier) {
    if (!rfq) return;
    setQuoteFor(s);
    setQuoteLines(Object.fromEntries(rfq.items.map((i) => {
      const q = s.quotes.find((x) => x.rfq_item_id === i.id);
      return [i.id, {
        unit_cost: q ? (q.unit_cost / subunit).toFixed(dp) : '',
        gst_rate: String(q?.gst_rate ?? 0),
        discount_type: q?.discount_type ?? 'PERCENTAGE',
        // PERCENTAGE is basis points (÷100 always); FLAT is minor units (÷subunit).
        discount_value: q && q.discount_value
          ? (q.discount_type === 'FLAT'
              ? (q.discount_value / subunit).toFixed(dp)
              : (q.discount_value / 100).toFixed(2)) // not-money: basis points → percent
          : '',
      }];
    })));
    setQuoteLeadTime(s.lead_time_days == null ? '' : String(s.lead_time_days));
    setQuoteNotes(s.notes ?? '');
  }
  function handleQuote(declined = false) {
    if (!rfq || !quoteFor) return;
    const quotedItems = declined ? [] : rfq.items.filter((i) => (quoteLines[i.id]?.unit_cost ?? '') !== '');
    for (const i of quotedItems) {
      const line = quoteLines[i.id];
      const discount = parseFloat(line.discount_value) || 0;
      const unitCost = Math.round(parseFloat(line.unit_cost) * subunit);
      if (discount < 0) { toast({ title: `${i.item_name}: discount cannot be negative`, variant: 'error' }); return; }
      if (line.discount_type === 'PERCENTAGE' && discount > 100) { toast({ title: `${i.item_name}: discount cannot exceed 100%`, variant: 'error' }); return; }
      if (line.discount_type === 'FLAT' && Math.round(discount * subunit) > unitCost) { toast({ title: `${i.item_name}: discount cannot exceed the unit price`, variant: 'error' }); return; }
    }
    const quotes = quotedItems.map((i) => {
      const line = quoteLines[i.id];
      const discount = parseFloat(line.discount_value) || 0;
      return {
        rfq_item_id: i.id,
        unit_cost: Math.round(parseFloat(line.unit_cost) * subunit),
        gst_rate: parseInt(line.gst_rate, 10) || 0,
        // PERCENTAGE → basis points (×100 always); FLAT → minor units (×subunit).
        ...(discount > 0
          ? {
              discount_type: line.discount_type,
              discount_value:
                line.discount_type === 'PERCENTAGE'
                  ? Math.round(discount * 100) // not-money: percent → basis points
                  : Math.round(discount * subunit),
            }
          : {}),
      };
    });
    if (!declined && quotes.length === 0) { toast({ title: 'Enter at least one unit price', variant: 'error' }); return; }
    quoteMutation.mutate({
      id: rfq.id, rfqSupplierId: quoteFor.id,
      payload: { quotes, lead_time_days: quoteLeadTime ? parseInt(quoteLeadTime, 10) : undefined, notes: quoteNotes.trim() || undefined, response_status: declined ? 'DECLINED' : 'QUOTED' },
    }, {
      onSuccess: () => { setQuoteFor(null); toast({ title: declined ? `${quoteFor.supplier_name} marked as declined` : `Quote recorded for ${quoteFor.supplier_name}`, variant: 'success' }); },
      onError: (err: Error) => toast({ title: err.message, variant: 'error' }),
    });
  }

  function handleAward() {
    if (!rfq) return;
    const payload = { awards: rfq.items.map((i) => ({ rfq_item_id: i.id, rfq_supplier_id: awards[i.id] || null })) };
    awardMutation.mutate({ id: rfq.id, payload }, {
      onSuccess: (res) => {
        setShowAward(false);
        const numbers = res.purchase_orders.map((po) => po.po_number).join(', ');
        toast({ title: `Created ${res.purchase_orders.length} purchase order${res.purchase_orders.length === 1 ? '' : 's'} (${numbers}) - RFQ closed`, variant: 'success' });
      },
      onError: (err: Error) => toast({ title: err.message, variant: 'error' }),
    });
  }

  function handleDownload() {
    if (!rfq) return;
    pdfMutation.mutate(rfq.id, {
      onSuccess: ({ blob, filename }) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = filename ?? `${rfq.rfq_number}.pdf`;
        document.body.appendChild(a); a.click(); document.body.removeChild(a);
        URL.revokeObjectURL(url);
      },
      onError: (err: Error) => toast({ title: err.message || 'Failed to download RFQ', variant: 'error' }),
    });
  }

  const timeline: TimelineItem[] = [
    { id: 'created', type: 'draft', action: 'RFQ created', timestamp: rfq.created_at, ...(rfq.created_by_name ? { actor: rfq.created_by_name } : {}) },
  ];
  for (const s of rfq.suppliers) {
    if (s.sent_at) timeline.push({ id: `sent-${s.id}`, type: 'approved', action: `Sent to ${s.supplier_name}`, description: s.sent_to === 'MANUAL' ? 'Shared manually' : `Email · ${s.sent_to}`, timestamp: s.sent_at });
    if (s.quoted_at) timeline.push({ id: `q-${s.id}`, type: s.response_status === 'DECLINED' ? 'rejected' : 'completed', action: `${s.supplier_name} ${s.response_status === 'DECLINED' ? 'declined' : 'quoted'}`, timestamp: s.quoted_at });
  }
  for (const po of rfq.purchase_orders) timeline.push({ id: `po-${po.id}`, type: 'inProgress', action: `PO ${po.po_number} raised for ${po.supplier_name}`, timestamp: po.created_at });
  if (rfq.closed_at) timeline.push({ id: 'closed', type: 'completed', action: 'RFQ closed', timestamp: rfq.closed_at });
  if (rfq.cancelled_at) timeline.push({ id: 'cancelled', type: 'cancelled', action: `Cancelled${rfq.cancel_reason ? `: ${rfq.cancel_reason}` : ''}`, timestamp: rfq.cancelled_at, ...(rfq.cancelled_by_name ? { actor: rfq.cancelled_by_name } : {}) });
  timeline.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());

  return (
    <>
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-teal-600 via-teal-500 to-brand-600 p-6 text-white shadow-[0_24px_60px_-24px_rgba(13,148,136,0.55)] mb-6 animate-fade-in">
        <div className="pointer-events-none absolute -top-24 -right-24 h-56 w-56 rounded-full bg-white/20 blur-3xl" />
        <div className="relative flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold tracking-tight font-mono">{rfq.rfq_number}</h1>
              <RfqStatusBadge status={rfq.status} />
            </div>
            <p className="text-teal-100 mt-1">{rfq.location_name} · Required by {fmtDate(rfq.expected_delivery_date)}</p>
          </div>
          <div className="text-right">
            <p className="text-3xl font-bold tabular-nums">{rfq.suppliers.filter((s) => s.response_status === 'QUOTED').length}<span className="text-lg text-teal-100">/{rfq.suppliers.length}</span></p>
            <p className="text-xs uppercase tracking-wide text-teal-100">Quotes received</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Suppliers & quotes comparison */}
          <div className={`${GLASS} p-6`}>
            <h3 className="text-sm font-semibold text-neutral-700 dark:text-neutral-200 mb-4">Suppliers & Quotes</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-neutral-100 dark:border-neutral-800 align-top">
                    <th className="text-left py-2 pr-3 text-xs font-medium text-neutral-500 uppercase tracking-wide min-w-[180px]">Item</th>
                    {rfq.suppliers.map((s) => (
                      <th key={s.id} className="text-right py-2 pl-3 min-w-[170px]">
                        <div className="text-sm font-semibold text-neutral-800 dark:text-neutral-100 normal-case">{s.supplier_name}</div>
                        <div className="mt-1 flex items-center justify-end gap-2">
                          <RfqResponseBadge status={s.response_status} />
                          {s.lead_time_days != null && <span className="text-[11px] text-neutral-500">{s.lead_time_days}d lead</span>}
                        </div>
                        <div className="text-[11px] font-normal text-neutral-500 normal-case tracking-normal">
                          {s.sent_at ? `Sent ${fmtDate(s.sent_at)}${s.sent_to === 'MANUAL' ? ' (manual)' : ''}` : 'Not sent'}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rfq.items.map((item) => (
                    <tr key={item.id} className="border-b border-neutral-50 dark:border-neutral-800/50 last:border-0">
                      <td className="py-2.5 pr-3">
                        <div className="font-medium text-neutral-800 dark:text-neutral-200">{item.item_name}</div>
                        <div className="text-xs text-neutral-500 tabular-nums">{item.quantity} {item.unit}</div>
                        {canAward && (
                          <label className="mt-1 inline-flex items-center gap-1.5 text-[11px] text-neutral-500 cursor-pointer">
                            <input type="radio" name={`award-${item.id}`} checked={!awards[item.id]} onChange={() => setAwardPicks((prev) => ({ ...prev, [item.id]: '' }))} className="accent-teal-600" />
                            Don&apos;t order
                          </label>
                        )}
                        {item.purchase_stats && (
                          <div className="mt-1 flex flex-wrap items-center gap-1 text-[11px] tabular-nums" title="Reference from past confirmed purchase orders (pre-tax unit cost)">
                            <span className="rounded-md bg-success-50 text-success-700 dark:bg-success-900/30 dark:text-success-300 px-1.5 py-0.5">Low {formatCurrency(item.purchase_stats.min_cost, currency)}</span>
                            <span className="rounded-md bg-brand-50 text-brand-700 dark:bg-brand-900/30 dark:text-brand-300 px-1.5 py-0.5">Avg {formatCurrency(item.purchase_stats.avg_cost, currency)}</span>
                            <span className="rounded-md bg-warning-50 text-warning-700 dark:bg-warning-900/30 dark:text-warning-300 px-1.5 py-0.5">High {formatCurrency(item.purchase_stats.max_cost, currency)}</span>
                            <span className="text-neutral-400">/ {item.unit} · {item.purchase_stats.purchase_count} past PO line{item.purchase_stats.purchase_count === 1 ? '' : 's'}</span>
                          </div>
                        )}
                      </td>
                      {rfq.suppliers.map((s) => {
                        const cell = quoteCell(item, s, currency);
                        const isLowest = cell && (lowest.get(item.id) ?? []).includes(s.id) && rfq.suppliers.filter((x) => quoteCell(item, x, currency)).length > 1;
                        const isPicked = canAward && awards[item.id] === s.id;
                        const isWinner = item.awarded_rfq_supplier_id === s.id;
                        const isLoser = isAwarded && !isWinner && cell !== null;
                        return (
                          <td key={s.id} className={`py-2.5 pl-3 text-right tabular-nums transition-all duration-200 ${isPicked || isWinner ? 'ring-2 ring-inset ring-teal-500 bg-teal-50 dark:bg-teal-900/20' : isLowest ? 'bg-success-50 dark:bg-success-900/20' : ''} ${isLoser ? 'opacity-50' : ''}`}>
                            {cell ? (
                              <>
                                {canAward && (
                                  <label className="flex items-center justify-end gap-1.5 text-[11px] font-medium text-teal-700 dark:text-teal-300 cursor-pointer mb-0.5">
                                    <input type="radio" name={`award-${item.id}`} checked={isPicked} onChange={() => setAwardPicks((prev) => ({ ...prev, [item.id]: s.id }))} className="accent-teal-600" aria-label={`Order ${item.item_name} from ${s.supplier_name}`} />
                                    Order here
                                  </label>
                                )}
                                {isWinner && (
                                  <span className="inline-block mb-0.5 rounded-full bg-teal-600 text-white px-2 py-0.5 text-[11px] font-semibold">Won</span>
                                )}
                                <div className={`font-semibold ${isLowest ? 'text-success-700 dark:text-success-300' : 'text-neutral-900 dark:text-neutral-100'}`}>{formatCurrency(cell.line_total, currency)}</div>
                                <div className="text-xs text-neutral-500">{formatCurrency(cell.unit_cost, currency)} / {item.unit}{cell.gst_rate > 0 ? ` + ${cell.gst_rate}% ${tax.taxName}` : ''}</div>
                                {cell.line_discount > 0 && (
                                  <span className="inline-block mt-1 rounded-md bg-violet-50 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300 px-1.5 py-0.5 text-[11px] font-medium">
                                    −{formatCurrency(cell.line_discount, currency)}{cell.discount_type === 'PERCENTAGE' ? ` (${cell.discount_value / 100}% off)` : ` (${formatCurrency(cell.discount_value, currency)} / ${item.unit} off)`}
                                  </span>
                                )}
                              </>
                            ) : (
                              <span className="text-neutral-400">—</span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t border-neutral-200 dark:border-neutral-700">
                    <td className="py-3 pr-3 text-sm font-semibold text-neutral-700 dark:text-neutral-200">Quoted total</td>
                    {rfq.suppliers.map((s) => {
                      const t = supplierTotal(rfq.items, s, currency);
                      return (
                        <td key={s.id} className="py-3 pl-3 text-right">
                          <div className="font-bold tabular-nums text-neutral-900 dark:text-white">{t.quoted_lines > 0 ? formatCurrency(t.total, currency) : '—'}</div>
                          {t.quoted_lines > 0 && t.quoted_lines < rfq.items.length && (
                            <div className="text-[11px] text-warning-600">{t.quoted_lines}/{rfq.items.length} lines quoted</div>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                  <tr>
                    <td />
                    {rfq.suppliers.map((s) => (
                      <td key={s.id} className="py-2 pl-3">
                        <div className="flex flex-col items-end gap-1.5">
                          {canQuote && (
                            <Button variant="secondary" size="sm" onClick={() => openQuote(s)}>
                              {s.response_status === 'QUOTED' ? 'Edit quote' : 'Enter quote'}
                            </Button>
                          )}
                        </div>
                      </td>
                    ))}
                  </tr>
                </tfoot>
              </table>
            </div>
            {canAward && (
              <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-xl bg-gradient-to-r from-teal-50 to-brand-50 dark:from-teal-900/20 dark:to-brand-900/20 border border-teal-100 dark:border-teal-800 px-4 py-3">
                <p className="text-sm text-neutral-700 dark:text-neutral-300">
                  Pick the supplier for each line (the lowest quote is chosen for you). One purchase order is created per supplier, with only the lines it won, and the RFQ is closed.
                </p>
                <Button variant="primary" disabled={awardGroups.length === 0} onClick={() => setShowAward(true)}>
                  <ClipboardIcon className="h-4 w-4 mr-1.5" />Create POs ({awardGroups.length})
                </Button>
              </div>
            )}
          </div>

          {/* Linked POs */}
          <div className={`${GLASS} p-6`}>
            <h3 className="text-sm font-semibold text-neutral-700 dark:text-neutral-200 mb-3">Purchase Orders ({rfq.purchase_orders.length})</h3>
            {rfq.purchase_orders.length === 0 ? (
              <p className="text-sm text-neutral-500 dark:text-neutral-400">No purchase orders raised from this RFQ yet.</p>
            ) : (
              <ul className="divide-y divide-neutral-100 dark:divide-neutral-800">
                {rfq.purchase_orders.map((po) => (
                  <li key={po.id} className="flex items-center justify-between py-2 text-sm">
                    <button type="button" onClick={() => navigate(`/inventory/purchase-orders/${po.id}`)} className="font-medium text-teal-600 dark:text-teal-400 hover:underline font-mono">{po.po_number}</button>
                    <span className="text-neutral-600 dark:text-neutral-300">{po.supplier_name}</span>
                    <span className="text-xs text-neutral-500">{po.status}</span>
                    <span className="font-semibold tabular-nums text-neutral-900 dark:text-white">{formatCurrency(po.total_amount, currency)}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className={`${GLASS} p-6`}>
            <h3 className="text-sm font-semibold text-neutral-700 dark:text-neutral-200 mb-4">Activity</h3>
            <Timeline items={timeline} />
          </div>
        </div>

        <div className="space-y-6">
          <div className={`${GLASS_FORM} p-5`}>
            <h3 className="text-xs font-semibold text-neutral-500 uppercase tracking-wide mb-2">Status</h3>
            <RfqStatusBadge status={rfq.status} size="md" />
            <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-3 leading-relaxed">{STATUS_DESCRIPTIONS[rfq.status]}</p>
            <dl className="mt-4 space-y-2 text-sm">
              <div className="flex justify-between"><dt className="text-neutral-500">Created</dt><dd className="tabular-nums text-neutral-800 dark:text-neutral-200">{fmtDate(rfq.created_at)}</dd></div>
              <div className="flex justify-between"><dt className="text-neutral-500">By</dt><dd className="text-neutral-800 dark:text-neutral-200">{rfq.created_by_name ?? '—'}</dd></div>
              {rfq.notes && <div><dt className="text-neutral-500 mb-0.5">Notes</dt><dd className="text-neutral-800 dark:text-neutral-200">{rfq.notes}</dd></div>}
            </dl>
          </div>

          {isManager && (
            <div className={`${GLASS_FORM} p-5`}>
              <h3 className="text-sm font-semibold text-neutral-700 dark:text-neutral-200 mb-3">Actions</h3>
              <div className="space-y-2">
                {canSend && (
                  <Button variant="primary" className="w-full" onClick={openSend}>
                    <SendIcon className="h-4 w-4 mr-1.5" />{rfq.status === 'SENT' ? 'Re-send to Suppliers' : 'Send to Suppliers'}
                  </Button>
                )}
                {canEdit && <Button variant="secondary" className="w-full" onClick={() => navigate(`/inventory/rfqs/${rfq.id}/edit`)}>Edit RFQ</Button>}
                <Button variant="secondary" className="w-full" onClick={handleDownload} loading={pdfMutation.isPending}>
                  <DownloadIcon className="h-4 w-4 mr-1.5" />Download RFQ
                </Button>
                {canClose && (
                  <Button variant="secondary" className="w-full" loading={closeMutation.isPending}
                    onClick={() => closeMutation.mutate(rfq.id, { onSuccess: () => toast({ title: 'RFQ closed', variant: 'success' }), onError: (e: Error) => toast({ title: e.message, variant: 'error' }) })}>
                    <CheckIcon className="h-4 w-4 mr-1.5" />Close RFQ
                  </Button>
                )}
                {canCancel && (
                  <Button variant="danger" className="w-full" onClick={() => setShowCancel(true)}>
                    <BanIcon className="h-4 w-4 mr-1.5" />Cancel RFQ
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Send modal */}
      <Modal open={showSend} onClose={() => setShowSend(false)} title="Send RFQ to suppliers"
        footer={<div className="flex justify-end gap-2"><Button variant="secondary" onClick={() => setShowSend(false)}>Cancel</Button><Button variant="primary" onClick={handleSend} loading={sendMutation.isPending}>{sendMode === 'MANUAL' ? 'Mark as Sent' : 'Send Emails'}</Button></div>}>
        <div className="space-y-3">
          {/* How the RFQ reaches suppliers: emailed from here, or the buyer downloads
              the PDF and sends it themselves (WhatsApp / print / phone). */}
          <div role="radiogroup" aria-label="Send method" className="grid grid-cols-2 gap-2">
            {([
              { value: 'EMAIL', label: 'Email from Orio360', hint: 'One email per supplier with the item list' },
              { value: 'MANUAL', label: "I'll send it myself", hint: 'Download the PDF; just record that it was sent' },
            ] as const).map((opt) => (
              <button key={opt.value} type="button" role="radio" aria-checked={sendMode === opt.value} onClick={() => setSendMode(opt.value)}
                className={`rounded-lg border p-3 text-left transition-colors duration-200 ${sendMode === opt.value ? 'border-teal-500 bg-teal-50 dark:bg-teal-900/20' : 'border-neutral-200 dark:border-neutral-700 hover:bg-neutral-50 dark:hover:bg-neutral-800'}`}>
                <div className="text-sm font-semibold text-neutral-900 dark:text-white">{opt.label}</div>
                <div className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5">{opt.hint}</div>
              </button>
            ))}
          </div>
          {sendMode === 'MANUAL' && (
            <div className="flex items-center justify-between gap-3 rounded-lg border border-neutral-200 dark:border-neutral-700 px-3 py-2">
              <p className="text-sm text-neutral-600 dark:text-neutral-400">All {rfq.suppliers.length} supplier{rfq.suppliers.length === 1 ? '' : 's'} will be marked as sent today.</p>
              <Button variant="secondary" size="sm" onClick={handleDownload} loading={pdfMutation.isPending}><DownloadIcon className="h-4 w-4 mr-1" />Download RFQ</Button>
            </div>
          )}
          {sendMode === 'EMAIL' && <p className="text-sm text-neutral-600 dark:text-neutral-400">One email per supplier with the item list (no prices). Suppliers without a valid address are skipped.</p>}
          {sendMode === 'EMAIL' && rfq.suppliers.map((s) => (
            <div key={s.id} className="grid grid-cols-5 items-center gap-2">
              <span className="col-span-2 text-sm font-medium text-neutral-800 dark:text-neutral-200 truncate">{s.supplier_name}</span>
              <div className="col-span-3">
                <Input type="email" value={sendEmails[s.id] ?? ''} onChange={(v) => setSendEmails((prev) => ({ ...prev, [s.id]: v }))} placeholder="supplier@example.com"
                  error={sendEmails[s.id] && !EMAIL_RE.test(sendEmails[s.id]) ? 'Invalid email' : undefined} />
              </div>
            </div>
          ))}
        </div>
      </Modal>

      {/* Quote modal */}
      <Modal open={quoteFor !== null} onClose={() => setQuoteFor(null)} title={quoteFor ? `Quote from ${quoteFor.supplier_name}` : ''} allowOverflow
        footer={<div className="flex justify-between gap-2">
          <Button variant="secondary" onClick={() => handleQuote(true)} disabled={quoteMutation.isPending}>Mark declined</Button>
          <div className="flex gap-2"><Button variant="secondary" onClick={() => setQuoteFor(null)}>Cancel</Button><Button variant="primary" onClick={() => handleQuote(false)} loading={quoteMutation.isPending}>Save quote</Button></div>
        </div>}>
        <div className="space-y-4">
          <table className="w-full table-fixed text-sm">
            <thead>
              <tr className="border-b border-neutral-200 dark:border-neutral-700">
                <th className="text-left py-2 pr-3 text-xs font-medium text-neutral-500 uppercase tracking-wide">Item</th>
                <th className="text-left py-2 pr-3 text-xs font-medium text-neutral-500 uppercase tracking-wide w-32">Unit price ({symbol})</th>
                <th className="text-left py-2 pr-3 text-xs font-medium text-neutral-500 uppercase tracking-wide w-24">{tax.taxName} %</th>
                <th className="text-left py-2 text-xs font-medium text-neutral-500 uppercase tracking-wide w-36">Discount / unit</th>
              </tr>
            </thead>
            <tbody>
              {rfq.items.map((i) => {
                const line = quoteLines[i.id] ?? blankQuoteLine();
                const patch = (p: Partial<QuoteLineForm>) => setQuoteLines((prev) => ({ ...prev, [i.id]: { ...(prev[i.id] ?? blankQuoteLine()), ...p } }));
                return (
                  <tr key={i.id} className="border-b border-neutral-100 dark:border-neutral-800/50 last:border-0">
                    <td className="py-2 pr-3"><div className="font-medium text-neutral-800 dark:text-neutral-200">{i.item_name}</div><div className="text-xs text-neutral-500">{i.quantity} {i.unit}</div></td>
                    <td className="py-2 pr-3"><Input type="number" value={line.unit_cost} onChange={(v) => patch({ unit_cost: v })} placeholder="0.00" /></td>
                    <td className="py-2 pr-3"><Select options={taxRateOptions(currency, line.gst_rate)} value={line.gst_rate} onChange={(v) => patch({ gst_rate: typeof v === 'string' ? v : '0' })} /></td>
                    <td className="py-2">
                      {/* %/₹ toggle + amount — same control as the PO wizard line discount. */}
                      <div className="flex items-stretch gap-1">
                        <div role="radiogroup" aria-label="Discount type" className="flex rounded-lg border border-neutral-300 dark:border-neutral-600 overflow-hidden shrink-0">
                          {(['PERCENTAGE', 'FLAT'] as const).map((t) => (
                            <button
                              key={t}
                              type="button"
                              role="radio"
                              aria-checked={line.discount_type === t}
                              onClick={() => patch({ discount_type: t })}
                              className={`px-2 text-xs font-semibold transition-colors duration-200 ${line.discount_type === t ? 'bg-teal-600 text-white' : 'bg-white dark:bg-neutral-800 text-neutral-500 hover:bg-teal-50 dark:hover:bg-neutral-700'}`}
                              title={t === 'PERCENTAGE' ? 'Percent off' : 'Rupees off per unit'}
                            >
                              {t === 'PERCENTAGE' ? '%' : symbol}
                            </button>
                          ))}
                        </div>
                        <Input type="number" value={line.discount_value} onChange={(v) => patch({ discount_value: v })} placeholder="0" className="flex-1 min-w-0" />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Lead time (days)</label>
              <Input type="number" value={quoteLeadTime} onChange={(v) => setQuoteLeadTime(v)} placeholder="e.g. 7" />
            </div>
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Notes</label>
              <Input value={quoteNotes} onChange={(v) => setQuoteNotes(v)} placeholder="Validity, terms…" />
            </div>
          </div>
        </div>
      </Modal>

      {/* Award modal */}
      <Modal open={showAward} onClose={() => setShowAward(false)} title="Create purchase orders"
        footer={<div className="flex justify-end gap-2"><Button variant="secondary" onClick={() => setShowAward(false)}>Back</Button>
          <Button variant="primary" onClick={handleAward} loading={awardMutation.isPending}>Create {awardGroups.length} PO{awardGroups.length === 1 ? '' : 's'} and close RFQ</Button></div>}>
        <div className="space-y-3">
          {awardGroups.map((g) => (
            <div key={g.supplier.id} className="rounded-lg border border-neutral-200 dark:border-neutral-700 p-3">
              <div className="flex items-center justify-between mb-1.5">
                <span className="font-semibold text-neutral-900 dark:text-white">{g.supplier.supplier_name}</span>
                <span className="font-bold tabular-nums text-teal-700 dark:text-teal-300">{formatCurrency(g.total, currency)}</span>
              </div>
              <ul className="space-y-0.5 text-sm">
                {g.lines.map((l) => (
                  <li key={l.item.id} className="flex justify-between text-neutral-600 dark:text-neutral-400">
                    <span>{l.item.item_name} · {l.item.quantity} {l.item.unit}</span>
                    <span className="tabular-nums">{formatCurrency(l.cell.line_total, currency)}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
          {rfq.items.some((i) => !awards[i.id]) && (
            <p className="text-sm text-warning-700 dark:text-warning-300">
              Not ordered: {rfq.items.filter((i) => !awards[i.id]).map((i) => i.item_name).join(', ')}
            </p>
          )}
          <p className="text-xs text-neutral-500 dark:text-neutral-400">The purchase orders are created as drafts. No more purchase orders can be raised from this RFQ afterwards.</p>
        </div>
      </Modal>

      {/* Cancel modal */}
      <Modal open={showCancel} onClose={() => setShowCancel(false)} title="Cancel RFQ"
        footer={<div className="flex justify-end gap-2"><Button variant="secondary" onClick={() => setShowCancel(false)}>Keep</Button>
          <Button variant="danger" disabled={!cancelReason.trim()} loading={cancelMutation.isPending}
            onClick={() => cancelMutation.mutate({ id: rfq.id, reason: cancelReason.trim() }, { onSuccess: () => { setShowCancel(false); toast({ title: 'RFQ cancelled', variant: 'success' }); }, onError: (e: Error) => toast({ title: e.message, variant: 'error' }) })}>
            Cancel RFQ
          </Button></div>}>
        <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Reason</label>
        <Input value={cancelReason} onChange={(v) => setCancelReason(v)} placeholder="Why is this RFQ being cancelled?" />
      </Modal>
    </>
  );
}

export function RfqDetailPage() {
  const { id } = useParams<{ id: string }>();
  return (
    <PageContainer title="RFQ Detail" breadcrumbs={[{ label: 'Inventory', href: '/inventory' }, { label: 'RFQs', href: '/inventory/rfqs' }, { label: id?.slice(0, 8).toUpperCase() ?? 'RFQ' }]}>
      <ErrorBoundary>
        <RfqDetailContent />
      </ErrorBoundary>
    </PageContainer>
  );
}
